import * as React from 'react';
import { gql, useQuery } from '@apollo/client';
import { DatabasesMap, WorkspaceWithSubscriptionRights } from 'typings';

type WorkspacesWithRightsQueryResult = {
  workspaceWithSubscriptionRights: WorkspaceWithSubscriptionRights[];
};

const GET_WORKSPACES_WITH_RIGHTS = gql`
  query getWorkspacesWithRights {
    workspaceWithSubscriptionRights {
      id
      name
      manageSubscription
      plan
    }
  }
`;

const useWorkspacesWithRights = (endpoint: string, token: string, databasesMap: DatabasesMap) => {
  const [workspacesMap, setWorkspacesMap] = React.useState<DatabasesMap>({});

  const { loading, error, refetch } = useQuery<WorkspacesWithRightsQueryResult>(GET_WORKSPACES_WITH_RIGHTS, {
    context: {
      uri: `${endpoint}/graphql`,
      skip: !token || !endpoint, // Skip the query if token or endpoint is not available
      headers: token ? { Authorization: `Bearer ${token}` } : undefined
    },
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      const workspacesWithRightsMap: DatabasesMap = {};
      const workspaceMap: Record<string, WorkspaceWithSubscriptionRights> = {};
      data.workspaceWithSubscriptionRights.forEach((workspace: WorkspaceWithSubscriptionRights) => {
        workspaceMap[workspace.id] = workspace;
      });
      Object.entries(databasesMap).forEach(([databaseID, database]) => {
        const workspace = workspaceMap[databaseID];
        if (workspace) {
          workspacesWithRightsMap[databaseID] = {
            ...database,
            manageSubscription: workspace.manageSubscription,
            plan: workspace.plan
          };
        }
      });
      setWorkspacesMap(workspacesWithRightsMap);
    },
    onError: (e) => {
      console.error('error', e); // eslint-disable-line no-console
    }
  });

  // Only refetch when token or endpoint changes, and not on initial mount
  React.useEffect(() => {
    if (token && endpoint) {
      refetch();
    }
  }, [token, endpoint]);

  return { loading, error, workspacesMap };
};

export default useWorkspacesWithRights;
